import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Header,
    Pagination,
    PropertyFilterProps,
    Table,
    Grid,
} from '@amzn/awsui-components-react-v3';
import { columnDefinitions } from './ActivityGroupListTableColumns';
import { ActivityGroupListColumnLabels } from '../../../../interfaces/activityGroup';
import { updateQueryTokens } from '../utils/query-utils';
import { useActivityGroupList } from '../hooks/useActivityGroupList';
import {
    fetchActivityGroups,
    selectActivityGroups,
    selectError,
    selectIsLoading,
    selectTotalActivityGroups,
    selectVisibleColumns,
    selectWrapLines,
    selectContentDensity,
    selectStripedRows,
    setVisibleColumns,
    setWrapLines,
    setContentDensity,
    setStripedRows,
    initializeUserPreference,
    updateUserPreference,
} from '../../../../store/slices/activityGroupListSlice';
import { selectIsLoading as selectIsUserLoading } from '../../../../../common/store/slices/userSlice';
import ActivityGroupListCollectionPreferences from '../components/List/ActivityGroupListCollectionPreferences';
import ActivityGroupListPropertyFilter from '../components/List/ActivityGroupListPropertyFilter';
import ActivityGroupListCalendarFilter from '../components/List/ActivityGroupListCalendarFilter';
import { useNotifications } from '../../../../../common/context/grimsbyNotifications';
import handleActivityListNotification from '../../../../../common/utils/handleActivityListNotification';

const ActivityGroupList: React.FC<{}> = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { addNotification } = useNotifications();

    const error = useSelector(selectError);
    const activityGroups = useSelector(selectActivityGroups);
    const isLoading = useSelector(selectIsLoading);
    const isUserLoading = useSelector(selectIsUserLoading);
    const totalCount = useSelector(selectTotalActivityGroups);
    const visibleColumns = useSelector(selectVisibleColumns);
    const wrapLines = useSelector(selectWrapLines);
    const contentDensity = useSelector(selectContentDensity);
    const stripedRows = useSelector(selectStripedRows);
    const [combinedQuery, setCombinedQuery] =
        useState<PropertyFilterProps.Query>({
            tokens: [],
            operation: 'and',
        });

    const {
        currentPageIndex,
        pagesCount,
        pageSize,
        buildUpdatedParams,
        setCurrentPageIndex,
        setPageSize,
        resetPagination,
    } = useActivityGroupList({
        combinedQuery,
        dispatch,
        totalCount,
    });

    useEffect(() => {
        dispatch(initializeUserPreference() as any);
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchActivityGroups(buildUpdatedParams()));
    }, [dispatch, combinedQuery, pageSize, currentPageIndex]);

    const handlePropertyFilterChange = useCallback(
        (query: PropertyFilterProps.Query) => {
            setCombinedQuery({
                ...combinedQuery,
                tokens: updateQueryTokens({
                    combinedQueryTokens: combinedQuery.tokens,
                    newTokens: [...query.tokens],
                    shouldIncludeDateRange: true,
                }),
            });
            resetPagination();
        },
        [combinedQuery],
    );

    const handlePageChange = (e: { detail: { currentPageIndex: number } }) => {
        setCurrentPageIndex(e.detail.currentPageIndex);
    };

    const handleContentSelectionChange = async (detail: any) => {
        const contentSelection = detail.contentDisplay
            ? detail.contentDisplay
                  .filter((item: any) => item.visible)
                  .map((item: any) => item.id)
            : detail.visibleContent;

        if (contentSelection) {
            const dispatchPromise = dispatch(
                updateUserPreference({
                    visible_columns: contentSelection,
                    page_size: detail.pageSize,
                    wrap_lines: detail.wrapLines,
                    content_density: detail.contentDensity,
                    striped_rows: detail.stripedRows,
                }) as any,
            );

            await handleActivityListNotification(
                dispatchPromise,
                addNotification,
            );

            dispatch(setVisibleColumns(contentSelection));
            if (detail.wrapLines !== undefined) {
                dispatch(setWrapLines(detail.wrapLines));
            }
            if (detail.contentDensity) {
                dispatch(setContentDensity(detail.contentDensity));
            }
            if (detail.stripedRows !== undefined) {
                dispatch(setStripedRows(detail.stripedRows));
            }
            if (detail.pageSize) {
                setPageSize(detail.pageSize);
            }
        }
    };

    const orderedColumnDefinitions = visibleColumns
        ? visibleColumns
              .map((colId) => columnDefinitions.find((col) => col.id === colId))
              .filter(Boolean)
        : columnDefinitions;

    return (
        <>
            {error && (
                <Alert header="Error" type="error">
                    {error}
                </Alert>
            )}
            <Table
                items={activityGroups}
                loading={isLoading || isUserLoading}
                stripedRows={stripedRows}
                contentDensity={contentDensity}
                wrapLines={wrapLines}
                resizableColumns={true}
                columnDefinitions={orderedColumnDefinitions}
                sortingDisabled
                visibleColumns={visibleColumns}
                data-testid="ActivityGroupListTable"
                loadingText="Loading activity groups"
                preferences={
                    <ActivityGroupListCollectionPreferences
                        preferences={{
                            contentDisplay: [
                                ...(visibleColumns?.map((id) => ({
                                    id,
                                    visible: true,
                                })) || []),
                                ...ActivityGroupListColumnLabels.filter(
                                    (label) => !visibleColumns?.includes(label),
                                ).map((label) => ({
                                    id: label,
                                    visible: false,
                                })),
                            ],
                            pageSize: pageSize,
                            wrapLines: wrapLines,
                            contentDensity: contentDensity,
                            stripedRows: stripedRows,
                        }}
                        onConfirm={handleContentSelectionChange}
                    />
                }
                pagination={
                    <Pagination
                        currentPageIndex={currentPageIndex}
                        pagesCount={pagesCount}
                        onChange={handlePageChange}
                    />
                }
                header={
                    <Header
                        counter={`(${totalCount})`}
                        actions={
                            <Button
                                onClick={() => {
                                    history.push('/activities/group/create');
                                }}
                                variant="primary"
                            >
                                Create Activity Group
                            </Button>
                        }
                    >
                        Activity Groups
                    </Header>
                }
                filter={
                    <Grid gridDefinition={[{ colspan: 4 }, { colspan: 7 }]}>
                        <div>
                            <ActivityGroupListPropertyFilter
                                onChange={handlePropertyFilterChange}
                            />
                        </div>
                        <ActivityGroupListCalendarFilter
                            setCombinedQuery={setCombinedQuery}
                            combinedQuery={combinedQuery}
                        />
                    </Grid>
                }
                empty={
                    <Box textAlign="center" color="inherit">
                        <Box padding={{ bottom: 's' }}>
                            No activity groups to display.
                        </Box>
                    </Box>
                }
            />
        </>
    );
};

export default ActivityGroupList;
